import { inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { MsalService } from '@azure/msal-angular';
import type { RedirectRequest } from '@azure/msal-browser';
import { v4 as uuidv4 } from 'uuid';
import {
  finalize,
  from,
  map,
  Observable,
  shareReplay,
  switchMap,
  throwError
} from 'rxjs';
import type {
  AuthStateSession,
  IDecodedUserToken,
  IUserInfo,
  UserProfile
} from '../type/auth.type';
import dayjs from 'dayjs';
import { AuthApiService } from '../api/auth.api.service';
import {
  createMsalInstance,
  loginRequest,
  msalConfig,
  setMsalRuntimeConfig
} from '../config/msal';
import { IGlobalState } from '../../../shared/provider/types/global-state';
import { Store } from '@ngrx/store';
import { environment } from '../../../../environments/environment';
import { jwtDecode } from 'jwt-decode';
import { UserProfileItem } from '../../../features/admin/users/type/users.type';

export interface AdminLoginCredentials {
  email: string;
  password: string;
}

export interface AuthSession {
  token: string;
  expiresAt: string;
  decodedToken: {
    nameid: string;
    sessionId: string;
    roleUserId: string;
    role: string;
    email: string;
    givenName: string;
    familyName: string;
    exp: number;
  };
}

export interface LoginResponse extends AuthSession {
  userProfile?: UserProfile;
}

export interface MicrosoftLoginOptions {
  loginHint?: string;
  clientId?: string;
  tenantId?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly store = inject(Store);
  private readonly msalService = inject(MsalService);
  private readonly authApiService = inject(AuthApiService);
  private readonly cookieService = inject(CookieService);
  private readonly appStateSignal = this.store.selectSignal(
    (state: IGlobalState | undefined) => state?.appState
  );
  private refreshRequest$: Observable<AuthStateSession> | null = null;


  adminLoginWithCredentials(credentials: AdminLoginCredentials): Observable<LoginResponse> {
    const deviceId = this.resolveDeviceId();
    this.persistDeviceIdCookie(deviceId);

    return this.authApiService.usernamePasswordLogin(
      credentials.email,
      credentials.password,
      deviceId
    ).pipe(
      map((response) => this.createLoginResponse(response.token, response.userProfile))
    );
  }

  /**
   * Starts the Microsoft login redirect flow.
    * Tokens are restored after redirect in init auth effects.
    * When backend clientId/tenantId are provided, they override defaults for this session,
    * and a runtime MSAL instance is initialized before redirect.
   */
  adminLoginWithMicrosoft(options?: MicrosoftLoginOptions): Observable<void> {
    const normalizedLoginHint = options?.loginHint?.trim();
    const normalizedClientId = options?.clientId?.trim();
    const normalizedTenantId = options?.tenantId?.trim();
    const hasClientIdOverride = normalizedClientId !== undefined && normalizedClientId.length > 0;
    const hasTenantIdOverride = normalizedTenantId !== undefined && normalizedTenantId.length > 0;
    const hasRuntimeOverrides = hasClientIdOverride || hasTenantIdOverride;

    if (hasRuntimeOverrides) {
      setMsalRuntimeConfig({
        clientId: normalizedClientId,
        tenantId: normalizedTenantId,
      });
    }

    const resolvedClientId = hasClientIdOverride ? normalizedClientId : msalConfig.clientId;
    const resolvedTenantId = hasTenantIdOverride ? normalizedTenantId : msalConfig.tenantId;

    const msalInstance = hasRuntimeOverrides
      ? createMsalInstance({
        clientId: resolvedClientId,
        tenantId: resolvedTenantId,
      })
      : this.msalService.instance;

    const request: RedirectRequest = { ...loginRequest };

    if (normalizedLoginHint !== undefined && normalizedLoginHint.length > 0) {
      request.loginHint = normalizedLoginHint;
    }

    if (resolvedTenantId.trim().length > 0) {
      request.authority = `https://login.microsoftonline.com/${resolvedTenantId}`;
    }

    if (!hasRuntimeOverrides) {
      return from(msalInstance.loginRedirect(request));
    }

    return from(msalInstance.initialize()).pipe(
      switchMap(() => from(msalInstance.loginRedirect(request)))
    );
  }

  validateMicrosoftToken(msalToken: string): Observable<LoginResponse> {
    return this.authApiService.validateEntraToken(msalToken).pipe(
      map((response) => this.createLoginResponse(response.token, response.userProfile))
    );
  }

  refreshAuthToken(): Observable<AuthStateSession> {
    if (this.refreshRequest$ !== null) {
      return this.refreshRequest$;
    }

    const token = this.getStoredAuthToken()?.trim() ?? '';

    if (token.length === 0) {
      return throwError(() => new Error('No backend access token is available for refresh'));
    }

    try {
      this.validateAuthSession(token, true);
    } catch (error) {
      this.deleteCookies();
      return throwError(() => error);
    }

    this.refreshRequest$ = this.authApiService.refreshToken(token).pipe(
      map((response) => {
        return this.setAuthToken(response.token, response.userProfile);
      }),
      finalize(() => {
        this.refreshRequest$ = null;
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    return this.refreshRequest$;
  }

  emailValidation(email: string): Observable<IUserInfo> {
    return this.authApiService.validateEmail(email);
  }

  adminLogoutWithMicrosoft(): void {
    const origin = globalThis.location?.origin;
    const postLogoutRedirectUri = origin !== undefined && origin !== null
      ? `${origin}/login`
      : '/login';

    this.msalService.logoutRedirect({ postLogoutRedirectUri });
  }

  setTokenNoInit(token: string): AuthSession {
    try {
      const session = this.validateAuthSession(token);

      this.persistAuthCookies(session.token, session.decodedToken.nameid);

      return session;
    } catch (error) {
      this.deleteCookies();
      throw error;
    }
  }

  setAuthToken(token: string, userProfile?: UserProfile | null): AuthStateSession {
    try {
      const session = this.validateAuthSession(token);
      const decodedToken = session.decodedToken;
      const sessionId = parseInt(decodedToken.sessionId, 10);

      this.persistAuthCookies(session.token, decodedToken.nameid);

      return {
        token: session.token,
        expiresAt: session.expiresAt,
        sessionId,
        role: decodedToken.role,
        userProfile: this.createUserProfileForAppState(userProfile, session),
      };
    } catch (error) {
      this.deleteCookies();
      throw error;
    }
  }

  validateAuthSession(token: string, allowExpired = false): AuthSession {
    const normalizedToken = token.trim();

    if (normalizedToken.length === 0) {
      throw new Error('Backend access token is required');
    }

    let decodedToken: unknown;

    try {
      decodedToken = jwtDecode<unknown>(normalizedToken);
    } catch {
      throw new Error('Backend access token is malformed');
    }

    if (decodedToken === null
      || typeof decodedToken !== 'object'
      || Array.isArray(decodedToken)) {
      throw new Error('Backend access token is malformed');
    }

    const claims = decodedToken as Record<string, unknown>;
    const nameid = this.getRequiredStringClaim(claims, 'nameid');
    const sessionIdClaim = this.getRequiredStringClaim(claims, 'sessionId');
    const roleUserId = this.getRequiredStringClaim(claims, 'roleUserId');
    const role = this.getRequiredStringClaim(claims, 'role');
    const email = this.getRequiredStringClaim(claims, 'email');
    const givenName = this.getOptionalStringClaim(claims, 'given_name');
    const familyName = this.getOptionalStringClaim(claims, 'family_name');
    const exp = claims['exp'];

    const sessionId = Number(sessionIdClaim);

    if (!Number.isSafeInteger(sessionId) || sessionId <= 0) {
      throw new Error('Backend access token has an invalid sessionId claim');
    }

    if (typeof exp !== 'number' || !Number.isFinite(exp) || exp <= 0) {
      throw new Error('Backend access token has an invalid exp claim');
    }

    const expiresAt = dayjs.unix(exp);

    if (!expiresAt.isValid()) {
      throw new Error('Backend access token has an invalid exp claim');
    }

    if (!allowExpired && !dayjs().isBefore(expiresAt)) {
      throw new Error('Backend access token is expired');
    }

    return {
      token: normalizedToken,
      expiresAt: expiresAt.toISOString(),
      decodedToken: {
        nameid,
        sessionId: sessionIdClaim,
        roleUserId,
        role,
        email,
        givenName,
        familyName,
        exp,
      },
    };
  }

  getStoredAuthToken(): string | null {
    const tokenFromStore = this.appStateSignal()?.accessToken;

    if (typeof tokenFromStore === 'string' && tokenFromStore.trim().length > 0) {
      return tokenFromStore;
    }

    const tokenFromCookies = this.cookieService.get(environment.authTokenKey).trim();
    return tokenFromCookies.length > 0 ? tokenFromCookies : '';
  }

  decodeAuthToken(token: string | undefined | null) {
    if (!token) {
      return null;
    }
    try {
      return jwtDecode<IDecodedUserToken>(token);
    } catch (error) { // eslint-disable-line
      console.error('Failed to decode token');
      return null;
    }
  }

  deleteCookies() {
    this.cookieService.delete(environment.authTokenKey, '/');
    this.cookieService.delete(environment.authRefreshTokenKey, '/');
    this.cookieService.delete('userId', '/');
    this.cookieService.delete('accountId', '/');
  }

  private persistAuthCookies(token: string, userId: string): void {
    const deviceId = this.resolveDeviceId();

    this.cookieService.set(environment.authTokenKey, token, 1, '/');
    this.cookieService.set(environment.deviceIdKey, deviceId, 1, '/');
    this.cookieService.set('userId', userId, 1, '/');
  }

  private resolveDeviceId(): string {
    const deviceId = this.cookieService.get(environment.deviceIdKey).trim();

    if (deviceId.length > 0) {
      return deviceId;
    }

    return uuidv4();
  }

  private persistDeviceIdCookie(deviceId: string): void {
    this.cookieService.set(environment.deviceIdKey, deviceId, 1, '/');
  }

  private createLoginResponse(
    token: string,
    userProfile?: UserProfile
  ): LoginResponse {
    return {
      ...this.validateAuthSession(token),
      userProfile,
    };
  }

  private getRequiredStringClaim(
    decodedToken: Record<string, unknown>,
    claim: 'nameid' | 'sessionId' | 'roleUserId' | 'role' | 'email'
  ): string {
    const value = decodedToken[claim];

    if (typeof value !== 'string' || value.trim().length === 0) {
      throw new Error(`Backend access token is missing required ${claim} claim`);
    }

    return value;
  }

  private getOptionalStringClaim(decodedToken: Record<string, unknown>, claim: string): string {
    const value = decodedToken[claim];
    return typeof value === 'string' ? value.trim() : '';
  }

  private createUserProfileForAppState(
    userProfile: UserProfile | null | undefined,
    session: AuthSession
  ): UserProfileItem {
    if (userProfile === undefined || userProfile === null) {
      const parsedUserId = Number.parseInt(session.decodedToken.nameid, 10);

      return {
        id: Number.isSafeInteger(parsedUserId) ? parsedUserId : 0,
        emailAddress: session.decodedToken.email,
        firstName: session.decodedToken.givenName,
        lastName: session.decodedToken.familyName,
        mobileNumber: '',
        status: 'Active',
        profilePictureFileName: null,
      };
    }

    return {
      id: userProfile.id,
      emailAddress: userProfile.emailAddress,
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      mobileNumber: userProfile.mobileNumber ?? '',
      status: userProfile.status ?? 'Active',
      profilePictureFileName: userProfile.profilePictureFileName,
    };
  }
}
